import { ReactElement, ReactNode, useId } from 'react';
import { OverlayTrigger, Popover } from 'react-bootstrap';
import { InfoIcon } from '../../bombiix-icon';

export type BombiixInfoPopoverPlacement = 'top' | 'bottom' | 'left' | 'right' | 'auto';

export interface BombiixInfoPopoverRow {
  label: string;
  value: ReactNode;
}

export interface BombiixInfoPopoverProps {
  rows: BombiixInfoPopoverRow[];
  placement?: BombiixInfoPopoverPlacement;
  ariaLabel?: string;
  buttonClassName?: string;
  iconSize?: number;
  children?: ReactNode;
}

const DEFAULT_ICON_SIZE = 20;

/**
 * Bombiix master-design info popover (Figma node 13564:539061). Renders a
 * label/value definition list on hover or keyboard focus, styled by
 * `assets/scss/master-design/_popover.scss`. Opens to the right and flips to
 * the left when the viewport edge leaves no room. Falls back to an InfoIcon trigger
 * when no custom `children` trigger is supplied.
 * @param {BombiixInfoPopoverProps} props - Rows to render plus trigger options.
 * @returns {ReactElement} The trigger wrapped in its overlay.
 */
export function BombiixInfoPopover({
  rows,
  placement = 'right',
  ariaLabel,
  buttonClassName = 'bombiix-icon-container',
  iconSize = DEFAULT_ICON_SIZE,
  children,
}: BombiixInfoPopoverProps): ReactElement {
  const popoverId = useId();
  const trigger = children ?? (
    <button type="button" className={buttonClassName} aria-label={ariaLabel}>
      <InfoIcon size={iconSize} />
    </button>
  );

  return (
    <OverlayTrigger
      placement={placement}
      flip
      trigger={['hover', 'focus']}
      rootClose
      overlay={
        <Popover id={popoverId} className="bombiix-info-popover bombiix-info-popover--auto">
          <Popover.Content>
            <dl>
              {rows.map((row) => (
                <div className="bombiix-info-popover__row" key={row.label}>
                  <dt>{row.label}</dt>
                  <dd>{row.value}</dd>
                </div>
              ))}
            </dl>
          </Popover.Content>
        </Popover>
      }
    >
      {trigger as ReactElement}
    </OverlayTrigger>
  );
}

export default BombiixInfoPopover;
